import {
  createFileRoute,
  Outlet,
  stripSearchParams,
} from '@tanstack/react-router'
import { z } from 'zod'

import type { ProjectId } from '@beaded/models'

import { projectQueries, siteQueries } from 'lib/queries'
import { getUIVersion } from 'lib/uiVersion'

// Read once at module load -- version never changes without a full page reload
const IS_CLASSIC_UI = getUIVersion() === 'classic'

const projectSearchSchema = IS_CLASSIC_UI
  ? z.object({
      tab: z.enum(['list', 'map', 'blueprint']).catch('list'),
    })
  : z.object({
      tab: z
        .enum([
          'overview',
          'sites',
          'loggers',
          'cables',
          'map',
          'blueprint',
          'settings',
        ])
        .catch('overview'),
    })

const projectSearchDefaults = IS_CLASSIC_UI
  ? ({ tab: 'list' } as const)
  : ({ tab: 'overview' } as const)

export const Route = createFileRoute('/_auth/projects/$id')({
  component: () => <Outlet />,
  // Custom validateSearch: validate known keys via Zod, but preserve unknown
  // keys (prefixed table params like sites_sort, loggers_q, etc.).
  validateSearch: (search: Record<string, unknown>) => ({
    ...search,
    ...projectSearchSchema.parse(search),
  }),
  search: {
    middlewares: [stripSearchParams(projectSearchDefaults)],
  },
  loader: ({ context: { queryClient }, params: { id } }) => {
    const projectId = Number(id) as ProjectId
    void queryClient.prefetchQuery(projectQueries.detail(projectId))
    void queryClient.prefetchQuery(siteQueries.byProject(projectId))
  },
})
